import { enumOptions } from "@dl/web-core-js/ui";
import i18next from "i18next";
import { IMAGE_TYPES, type ImageTypeOrderDTO } from "../../api/product";
import type { ShopifyImageRuleDTO } from "../../api/shopify";
import { NexpimView } from "../../views";
import { makeConfigGrid } from "./makeConfigPage";

const typeLabel = (type: string) => enumOptions(IMAGE_TYPES).find((option) => option.value === type)?.label ?? type;

/**
 * The business order of image types: how every product's Images tab groups its
 * images. One fixed row per type, so the drag order is the whole configuration.
 */
export const ImageTypeOrderGrid = makeConfigGrid<ImageTypeOrderDTO>({
  path: "/product/image-type-order",
  cacheKey: "image-type-order",
  titleKey: "settings.image-types.title",
  titleFallback: "Image types",
  view: NexpimView.PRODUCTS,
  columns: (c) => [
    c.text("image_type", (row) => typeLabel(row.image_type), { headerKey: "settings.image-types.type", priority: 1 }),
  ],
  order: {
    get: (row) => row.sort_order,
    set: (row, order) => ({ ...row, sort_order: order }),
  },
});

/**
 * One Shopify image rule list. Each row takes up to its quantity of images of
 * its type (and orientation, when set) that an earlier row has not taken, in
 * the product's own order; the drag order is the rule order.
 */
function makeRuleGrid(productType: "SINGLE_ITEM" | "BUNDLE", path: string) {
  return makeConfigGrid<ShopifyImageRuleDTO>({
    path,
    cacheKey: `shopify-image-rule-${productType}`,
    titleKey: "settings.shopify-images.title",
    titleFallback: "Shopify images",
    view: NexpimView.PRODUCTS,
    columns: (c, t) => [
      c.text("image_type", (row) => typeLabel(row.image_type), { headerKey: "settings.shopify-images.type", priority: 1 }),
      c.text(
        "orientation",
        (row) =>
          row.orientation === "FRONT"
            ? t("product.images.orientation.front", "Front")
            : row.orientation === "ANGLED"
              ? t("product.images.orientation.angled", "Angled")
              : row.orientation === "NA"
                ? t("product.images.orientation.na", "Not set")
                : t("settings.shopify-images.any", "Any"),
        { headerKey: "settings.shopify-images.orientation", priority: 1 },
      ),
      c.text("quantity", (row) => String(row.quantity), { headerKey: "settings.shopify-images.quantity", priority: 1 }),
    ],
    order: {
      get: (row) => row.sort_order,
      set: (row, order) => ({ ...row, sort_order: order }),
    },
    editor: {
      fields: {
        image_type: {
          kind: "select",
          get: (row) => row.image_type,
          set: (row, value) => ({ ...row, image_type: value }),
          options: enumOptions(IMAGE_TYPES),
          required: true,
        },
        orientation: {
          kind: "select",
          get: (row) => row.orientation ?? "",
          set: (row, value) => ({ ...row, orientation: value === "" ? null : (value as ShopifyImageRuleDTO["orientation"]) }),
          // A function, so the labels follow the language picked at run time.
          options: () => [
            { value: "", label: i18next.t("settings.shopify-images.any", "Any") },
            { value: "FRONT", label: i18next.t("product.images.orientation.front", "Front") },
            { value: "ANGLED", label: i18next.t("product.images.orientation.angled", "Angled") },
            { value: "NA", label: i18next.t("product.images.orientation.na", "Not set") },
          ],
        },
        quantity: {
          kind: "number",
          get: (row) => row.quantity,
          set: (row, value) => ({ ...row, quantity: Math.max(0, Number.parseInt(value, 10) || 0) }),
          required: true,
        },
      },
      newRow: () => ({ id: 0, product_type: productType, image_type: "WHITE_BACKGROUND", orientation: null, quantity: 1, sort_order: 999 }),
      canDelete: true,
    },
  });
}

export const ShopifySingleItemImageRuleGrid = makeRuleGrid("SINGLE_ITEM", "/shopify/image-rule/single-item");
export const ShopifyBundleImageRuleGrid = makeRuleGrid("BUNDLE", "/shopify/image-rule/bundle");
